import { useState, useCallback, useEffect } from "react";
import {
  generateId,
  ensureUniqueSlug,
  findFreePosition,
  loadAnalystOverride,
  saveAnalystOverride,
} from "@/lib/dashboardStore";
import { useDataService } from "@/lib/dataService";
import { useDashboardsQuery, useDashboardQuery } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import { placementKey } from "@/types/widget";
import type { Dashboard, DashboardSection, DashboardSettingsPatch } from "@/types/dashboard";
import type { WidgetPlacement } from "@/types/widget";
import { widgetRegistry } from "@/widgets/registry";

export function useSectionDashboard(dashboardSlug: string, scope: "admin" | "user" = "admin") {
  const ds = useDataService();
  const queryClient = useQueryClient();

  const { data: allDashboards } = useDashboardsQuery();
  const dashboardId = allDashboards?.find((d) => d.slug === dashboardSlug)?.id;

  const [config, setConfig] = useState<Dashboard | null>(null);

  const {
    data: dashboardData,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useDashboardQuery(dashboardId);

  const isLoading =
    allDashboards === undefined ||
    (!!dashboardId && !isDetailError && (isDetailLoading || !config));

  useEffect(() => {
    if (dashboardData) {
      if (scope === "user") {
        const override = loadAnalystOverride(dashboardData.id);
        setConfig(
          override.sections ? { ...dashboardData, sections: override.sections } : dashboardData,
        );
      } else {
        setConfig(dashboardData);
      }
    }
  }, [dashboardData, scope]);

  // Mutations

  function mutate(updater: (prev: Dashboard) => Dashboard) {
    setConfig((prev) => {
      if (!prev) return prev;
      return updater(prev);
    });
  }

  function mutateSections(updater: (sections: DashboardSection[]) => DashboardSection[]) {
    mutate((c) => ({ ...c, sections: updater(c.sections) }));
  }

  // Persistence

  const save = useCallback(
    async (dashboard: Dashboard) => {
      if (scope === "user") {
        const override = loadAnalystOverride(dashboard.id);
        saveAnalystOverride({ ...override, sections: dashboard.sections });
      } else {
        const saved = await ds.updateDashboard(dashboard.id, dashboard);
        queryClient.setQueryData(["dashboards", saved.id], saved);
        queryClient.setQueryData<Dashboard[]>(["dashboards"], (prev) =>
          prev ? prev.map((d) => (d.id === saved.id ? saved : d)) : prev,
        );
      }
    },
    [ds, queryClient, scope],
  );

  const updateSettings = useCallback(
    async (patch: DashboardSettingsPatch) => {
      if (!config) return;
      const existingSlugs = (allDashboards ?? [])
        .filter((d) => d.id !== config.id)
        .map((d) => d.slug);
      const slug =
        patch.slug && patch.slug !== config.slug
          ? ensureUniqueSlug(patch.slug, existingSlugs)
          : config.slug;
      const updated: Dashboard = { ...config, ...patch, slug };
      setConfig(updated);
      await save(updated);
    },
    [config, save, allDashboards],
  );

  // Section operations

  const addSection = useCallback((name = "New Section") => {
    mutateSections((sections) => [
      ...sections,
      { id: generateId("sec"), name, order: sections.length, collapsed: false, placements: [] },
    ]);
  }, []);

  const updateSection = useCallback(
    (sectionId: string, patch: Partial<Pick<DashboardSection, "name" | "collapsed">>) => {
      mutateSections((sections) =>
        sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)),
      );
    },
    [],
  );

  const deleteSection = useCallback((sectionId: string) => {
    mutateSections((sections) =>
      sections.filter((s) => s.id !== sectionId).map((s, i) => ({ ...s, order: i })),
    );
  }, []);

  const moveSectionUp = useCallback((sectionId: string) => {
    mutateSections((sections) => {
      const sorted = [...sections].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((s) => s.id === sectionId);
      if (idx <= 0) return sections;
      [sorted[idx - 1], sorted[idx]] = [sorted[idx], sorted[idx - 1]];
      return sorted.map((s, i) => ({ ...s, order: i }));
    });
  }, []);

  const moveSectionDown = useCallback((sectionId: string) => {
    mutateSections((sections) => {
      const sorted = [...sections].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((s) => s.id === sectionId);
      if (idx < 0 || idx >= sorted.length - 1) return sections;
      [sorted[idx], sorted[idx + 1]] = [sorted[idx + 1], sorted[idx]];
      return sorted.map((s, i) => ({ ...s, order: i }));
    });
  }, []);

  // Widget operations

  const updateSectionPlacements = useCallback(
    (sectionId: string, placements: WidgetPlacement[]) => {
      mutateSections((sections) =>
        sections.map((s) => (s.id === sectionId ? { ...s, placements } : s)),
      );
    },
    [],
  );

  const addWidgetToSection = useCallback((sectionId: string, widgetId: string) => {
    mutateSections((sections) =>
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        const entry = widgetRegistry.get(widgetId);
        if (!entry) return s;
        const m = entry.meta;
        const existsAlready = s.placements.some((p) => p.widgetId === widgetId);
        const instanceId = existsAlready ? generateId(widgetId) : undefined;
        const key = instanceId ?? widgetId;
        const { x, y } = findFreePosition(s.placements, m.defaultW, m.defaultH);
        const placement: WidgetPlacement = {
          widgetId,
          instanceId,
          layout: { i: key, x, y, w: m.defaultW, h: m.defaultH, minW: m.minW, minH: m.minH },
        };
        return { ...s, placements: [...s.placements, placement] };
      }),
    );
  }, []);

  const renameWidget = useCallback((sectionId: string, instanceId: string, title: string) => {
    mutateSections((sections) =>
      sections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              placements: s.placements.map((p) =>
                placementKey(p) === instanceId ? { ...p, title } : p,
              ),
            }
          : s,
      ),
    );
  }, []);

  const duplicateWidget = useCallback((sectionId: string, instanceId: string) => {
    mutateSections((sections) =>
      sections.map((s) => {
        if (s.id !== sectionId) return s;
        const original = s.placements.find((p) => placementKey(p) === instanceId);
        if (!original) return s;
        const newKey = generateId(original.widgetId);
        const { x, y } = findFreePosition(s.placements, original.layout.w, original.layout.h);
        const copy: WidgetPlacement = {
          ...original,
          instanceId: newKey,
          layout: { ...original.layout, i: newKey, x, y },
        };
        return { ...s, placements: [...s.placements, copy] };
      }),
    );
  }, []);

  const removeWidget = useCallback((sectionId: string, instanceId: string) => {
    mutateSections((sections) =>
      sections.map((s) =>
        s.id === sectionId
          ? { ...s, placements: s.placements.filter((p) => placementKey(p) !== instanceId) }
          : s,
      ),
    );
  }, []);

  const moveWidgetToSection = useCallback(
    (fromSectionId: string, instanceId: string, toSectionId: string) => {
      mutateSections((sections) => {
        const placement = sections
          .find((s) => s.id === fromSectionId)
          ?.placements.find((p) => placementKey(p) === instanceId);
        if (!placement) return sections;
        return sections.map((s) => {
          if (s.id === fromSectionId)
            return { ...s, placements: s.placements.filter((p) => placementKey(p) !== instanceId) };
          if (s.id === toSectionId) {
            const { x, y } = findFreePosition(s.placements, placement.layout.w, placement.layout.h);
            return {
              ...s,
              placements: [
                ...s.placements,
                { ...placement, layout: { ...placement.layout, x, y } },
              ],
            };
          }
          return s;
        });
      });
    },
    [],
  );

  const resetUserSections = useCallback(() => {
    if (scope !== "user" || !dashboardData) return;
    const override = loadAnalystOverride(dashboardData.id);
    saveAnalystOverride({ ...override, sections: undefined });
    setConfig(dashboardData);
  }, [scope, dashboardData]);

  // Derived

  const sortedSections = config ? [...config.sections].sort((a, b) => a.order - b.order) : [];

  return {
    config,
    isLoading,
    sortedSections,
    save,
    resetUserSections: scope === "user" ? resetUserSections : undefined,
    updateSettings,
    addSection,
    updateSection,
    deleteSection,
    moveSectionUp,
    moveSectionDown,
    updateSectionPlacements,
    addWidgetToSection,
    renameWidget,
    duplicateWidget,
    removeWidget,
    moveWidgetToSection,
  };
}
